import { ReactElement, useState, useEffect, useRef } from 'react';
import { Swiper as ReactSwiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y } from 'swiper/modules';
import { SwiperOptions } from 'swiper/types';
import 'swiper/css';
import 'swiper/css/pagination';
import useSwiperRef from '~/ui/components/common/Swiper/useSwiperRef';
import classnames from 'classnames';

interface SwiperProps extends SwiperOptions {
  slides: any[];
  renderSlide?: (slide: any) => ReactElement;
  className?: string; // Add className property
  slideClassName?: (slide: any) => string; // Add slideClassName property
  navigation?: boolean; // Make navigation optional
  pagination?: boolean; // Make pagination optional
  arrowBorder?: boolean;
  blackBackground?: boolean;
}

const Swiper = ({
  slides,
  renderSlide,
  className,
  slideClassName,
  navigation, // Destructure navigation
  pagination, // Destructure pagination
  arrowBorder,
  blackBackground,
  ...swiperProps
}: SwiperProps): ReactElement => {
  const [nextEl, nextElRef] = useSwiperRef();
  const [prevEl, prevElRef] = useSwiperRef();
  const paginationRef = useRef<HTMLDivElement | null>(null);

  const [swiperInstance, setSwiperInstance] = useState<any>(null);

  useEffect(() => {
    if (swiperInstance && paginationRef.current && pagination) {
      swiperInstance.params.pagination.el = paginationRef.current;

      if (swiperInstance.pagination) {
        swiperInstance.pagination.destroy();
        swiperInstance.pagination.init();
        swiperInstance.pagination.update();
      }
    }
  }, [swiperInstance, pagination]);

  return (
    <>
      <ReactSwiper
        modules={[Navigation, Pagination, A11y]}
        navigation={
          navigation
            ? {
                prevEl,
                nextEl,
              }
            : undefined
        } // Conditionally include navigation
        pagination={
          pagination
            ? {
                el: paginationRef.current,
                type: 'fraction',
                renderFraction: (currentClass, totalClass) =>
                  `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`,
              }
            : undefined
        } // Conditionally include pagination
        {...swiperProps}
        onSwiper={setSwiperInstance}
        className={className} // Pass className to ReactSwiper
      >
        {slides.map(slide => (
          <SwiperSlide key={slide.id} className={slideClassName ? slideClassName(slide) : ''}>
            {renderSlide(slide)}
          </SwiperSlide>
        ))}
      </ReactSwiper>
      {navigation && (
        <div className="swiper-actions swiper-buttons">
          <button
            type="button"
            className={classnames('swiper-button swiper-button-prev', {
              border: arrowBorder,
              black: blackBackground,
            })}
            ref={prevElRef}
            aria-label="Previous button"
          >
            <i className="icon-arrow" />
          </button>
          {pagination && <div className="carousel-pages" ref={paginationRef} />}
          <button
            type="button"
            className={classnames('swiper-button swiper-button-next', {
              border: arrowBorder,
              black: blackBackground,
            })}
            ref={nextElRef}
            aria-label="Next button"
          >
            <i className="icon-arrow" />
          </button>
        </div>
      )}
    </>
  );
};

export default Swiper;
